import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  CreditCardIcon,
  MapPinIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { useCart, CartItem } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../firebase/config';
import StripeService, { buildVendorSplitsFromCartGroups } from '../services/stripeService';
import OrderService from '../services/orderService';
import { sumLineShippingCosts } from '../utils/productShipping';
import './CheckoutModal.css';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (orderId: string) => void;
}

interface ShippingAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { cartItems, grandTotal, shippingTotal } = useCart();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'address' | 'payment'>('address');
  
  // Form state
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'United Kingdom',
  });
  
  const [useSameAddress, setUseSameAddress] = useState(true);
  const [customerPhone, setCustomerPhone] = useState('');

  const requiresShipping = cartItems.some((item) => !item.isDigital);

  // Initialize Stripe
  useEffect(() => {
    if (isOpen) {
      setStep(requiresShipping ? 'address' : 'payment');
      const initializeStripe = async () => {
        try {
          const stripeService = StripeService.getInstance();
          await stripeService.initialize();
        } catch (error) {
          console.error('Error initializing Stripe:', error);
        }
      };
      initializeStripe();
    }
  }, [isOpen, requiresShipping]);

  // Group items by vendor for display
  const vendorGroups = cartItems.reduce((groups, item) => {
    if (!groups[item.vendorId]) {
      groups[item.vendorId] = {
        vendorId: item.vendorId,
        items: [],
        subtotal: 0,
        shippingCost: 0,
        total: 0,
      };
    }
    groups[item.vendorId].items.push(item);
    groups[item.vendorId].subtotal += item.total;
    groups[item.vendorId].shippingCost = sumLineShippingCosts(groups[item.vendorId].items);
    groups[item.vendorId].total = groups[item.vendorId].subtotal + groups[item.vendorId].shippingCost;
    return groups;
  }, {} as Record<string, { vendorId: string; items: CartItem[]; subtotal: number; shippingCost: number; total: number }>);

  const handleAddressSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('payment');
  };

  const handlePayment = async () => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const stripeService = StripeService.getInstance();
      const orderService = OrderService.getInstance();
      const vendorSplits = buildVendorSplitsFromCartGroups(vendorGroups);

      const { data: profile } = await supabase
        .from('users')
        .select('email, display_name')
        .eq('id', user.uid)
        .maybeSingle();

      const customerEmail = profile?.email || user.email || '';
      const customerName = profile?.display_name || user.displayName || '';

      const orderShippingAddress = requiresShipping
        ? shippingAddress
        : {
            street: 'Digital delivery',
            city: 'N/A',
            state: 'N/A',
            postalCode: 'N/A',
            country: 'United Kingdom',
          };

      const order = await orderService.prepareCheckoutOrder(
        cartItems,
        {
          customerId: user.uid,
          customerEmail,
          customerName,
          customerPhone,
        },
        orderShippingAddress
      );

      const lineItems = cartItems.map((item) => ({
        name: item.name,
        unitAmount: Math.round(item.price * 100),
        quantity: item.quantity,
      }));

      if (shippingTotal > 0) {
        lineItems.push({
          name: 'Shipping',
          unitAmount: Math.round(shippingTotal * 100),
          quantity: 1,
        });
      }

      const { url } = await stripeService.createCheckoutSession({
        orderId: order.id,
        amount: Math.round(grandTotal * 100),
        currency: 'gbp',
        customerEmail,
        customerName,
        vendorSplits,
        metadata: {
          vendorIds: Object.keys(vendorGroups).join(','),
          customerId: user.uid,
        },
        lineItems,
      });

      window.location.assign(url);
    } catch (error) {
      console.error('Checkout error:', error);
      setError(error instanceof Error ? error.message : 'Checkout failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatPrice = (price: number) => `£${price.toFixed(2)}`;

  if (!isOpen) return null;

  return (
    <div className="checkout-overlay" role="dialog" aria-modal="true">
      <div className="checkout-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="checkout-modal">
        <div className="checkout-header">
          <h2 className="checkout-title">Checkout</h2>
          <button className="checkout-close-btn" onClick={onClose}>
            <XMarkIcon className="close-icon" />
          </button>
        </div>

        <div className="checkout-content">
          {step === 'address' && (
            <form className="checkout-form" onSubmit={handleAddressSubmit}>
              <div className="form-section">
                <h3 className="section-title">
                  <UserIcon className="section-icon" />
                  Customer Information
                </h3>
                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="+44 123 456 7890"
                  />
                </div>
              </div>

              {requiresShipping && (
              <div className="form-section">
                <h3 className="section-title">
                  <MapPinIcon className="section-icon" />
                  Shipping Address
                </h3>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Street Address *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={shippingAddress.street}
                      onChange={(e) => setShippingAddress({...shippingAddress, street: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">City *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State/County *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={shippingAddress.state}
                      onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Postal Code *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={shippingAddress.postalCode}
                      onChange={(e) => setShippingAddress({...shippingAddress, postalCode: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Country *</label>
                    <select
                      className="form-select"
                      value={shippingAddress.country}
                      onChange={(e) => setShippingAddress({...shippingAddress, country: e.target.value})}
                      required
                    >
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="United States">United States</option>
                      <option value="Canada">Canada</option>
                      <option value="Australia">Australia</option>
                    </select>
                  </div>
                </div>
              </div>
              )}

              <div className="form-section">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="useSameAddress"
                    checked={useSameAddress}
                    onChange={(e) => setUseSameAddress(e.target.checked)}
                  />
                  <label htmlFor="useSameAddress" className="checkbox-label">
                    Use same address for billing
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="continue-btn">
                  Continue to Payment
                </button>
              </div>
            </form>
          )}

          {step === 'payment' && (
            <div className="payment-section">
              <div className="order-summary">
                <h3 className="summary-title">Order Summary</h3>
                {Object.values(vendorGroups).map((vendorGroup, index) => (
                  <div key={vendorGroup.vendorId} className="vendor-group">
                    <h4 className="vendor-name">Vendor {index + 1}</h4>
                    {vendorGroup.items.map((item) => (
                      <div key={item.id} className="order-item">
                        <div className="item-info">
                          <span className="item-name">{item.name}</span>
                          <span className="item-quantity">x{item.quantity}</span>
                        </div>
                        <span className="item-price">{formatPrice(item.total)}</span>
                      </div>
                    ))}
                    <div className="vendor-totals">
                      <div className="total-row">
                        <span>Subtotal:</span>
                        <span>{formatPrice(vendorGroup.subtotal)}</span>
                      </div>
                      <div className="total-row">
                        <span>Shipping:</span>
                        <span>{formatPrice(vendorGroup.shippingCost)}</span>
                      </div>
                      <div className="total-row vendor-total">
                        <span>Total:</span>
                        <span>{formatPrice(vendorGroup.total)}</span>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="grand-total">
                  <span>Grand Total:</span>
                  <span>{formatPrice(grandTotal)}</span>
                </div>
              </div>

              <div className="payment-methods">
                <h3 className="payment-title">
                  <CreditCardIcon className="section-icon" />
                  Payment Method
                </h3>
                <div className="stripe-payment-element">
                  <p className="payment-note">
                    You will be redirected to Stripe for secure payment processing.
                  </p>
                </div>
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  className="back-btn" 
                  onClick={() => setStep('address')}
                >
                  Back to Address
                </button>
                <button 
                  type="button" 
                  className="pay-btn"
                  onClick={handlePayment}
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Processing...' : `Pay ${formatPrice(grandTotal)}`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;

